import React from 'react';
import { observer } from "mobx-react"
import ewtStore from '../EWTStore';
import { StickyTable, Row, Cell } from 'react-sticky-table';
import { Button, Input } from 'antd';
import { EditFilled } from '@ant-design/icons';

const buildScoreSnapshot = (roundID, players) => {
	const snapshot = {};
	(players || []).forEach((player) => {
		snapshot[player.id] = {};
		for (let hole = 1; hole <= 18; hole++) {
			const holeScore =
				ewtStore.roundScores[roundID] &&
				ewtStore.roundScores[roundID][player.id] &&
				ewtStore.roundScores[roundID][player.id][hole];
			snapshot[player.id][hole] =
				holeScore && holeScore.score != null ? String(holeScore.score) : '';
		}
	});
	return snapshot;
};

const parseDraftScore = (value) => {
	if (value === '' || value == null) return null;
	const parsed = parseInt(String(value).trim(), 10);
	return isNaN(parsed) ? null : parsed;
};

const getDraftTotals = (draftScores, playerID) => {
	if (!draftScores || !draftScores[playerID]) {
		return { F: '', B: '', T: '' };
	}
	let front = 0;
	let back = 0;
	let hasFront = false;
	let hasBack = false;
	for (let hole = 1; hole <= 9; hole++) {
		const score = parseDraftScore(draftScores[playerID][hole]);
		if (score != null) {
			front += score;
			hasFront = true;
		}
	}
	for (let hole = 10; hole <= 18; hole++) {
		const score = parseDraftScore(draftScores[playerID][hole]);
		if (score != null) {
			back += score;
			hasBack = true;
		}
	}
	return {
		F: hasFront ? front : '',
		B: hasBack ? back : '',
		T: hasFront || hasBack ? front + back : '',
	};
};

const CourseCard = observer(class CourseCard extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			editMode: false,
			draftScores: null,
			originalScores: null,
			saving: false,
		};
		this.enterEditMode = this.enterEditMode.bind(this);
		this.cancelEdit = this.cancelEdit.bind(this);
		this.saveCorrections = this.saveCorrections.bind(this);
		this.updateDraftScore = this.updateDraftScore.bind(this);
	}

	componentDidMount() {
		this.loadRoundData(this.getRoundID());
	}

	componentDidUpdate(prevProps) {
		const roundID = this.getRoundID();
		const prevRoundID =
			prevProps.tieBreakerMode
				? ewtStore.currentRoundID
				: prevProps.roundID === "" && ewtStore.currentRoundID
				? ewtStore.currentRoundID
				: prevProps.roundID;
		if (roundID && roundID !== prevRoundID) {
			this.loadRoundData(roundID);
		}
		if (
			prevProps.roundID !== this.props.roundID &&
			this.state.editMode &&
			!this.props.tieBreakerMode
		) {
			this.cancelEdit();
		}
	}

	loadRoundData(roundID) {
		if (!roundID) return;
		ewtStore.ensureRoundScoresLoaded(roundID);
		const round = ewtStore.rounds && ewtStore.rounds[roundID];
		if (round && round.courseID) {
			ewtStore.ensureCourse(round.courseID);
		}
	}

	getRoundID() {
		if (this.props.tieBreakerMode) return ewtStore.currentRoundID;
		let roundID = this.props.roundID;
		if (roundID === "" && ewtStore.currentRoundID) roundID = ewtStore.currentRoundID;
		return roundID;
	}

	getSortedPlayers() {
		const newPlayers = Object.keys(ewtStore.players).map(
			(playerID) => ewtStore.players[playerID]
		);
		return newPlayers.sort(function (a, b) {
			const aa = a.last_name + "_" + a.first_name;
			const bb = b.last_name + "_" + b.first_name;
			if (aa > bb) return 1;
			if (aa < bb) return -1;
			return 0;
		});
	}

	enterEditMode(players, roundID) {
		const snapshot = buildScoreSnapshot(roundID, players);
		this.setState({
			editMode: true,
			draftScores: JSON.parse(JSON.stringify(snapshot)),
			originalScores: snapshot,
			saving: false,
		});
	}

	cancelEdit() {
		this.setState({
			editMode: false,
			draftScores: null,
			originalScores: null,
			saving: false,
		});
	}

	updateDraftScore(playerID, holeNumber, value) {
		if (!/^\d*$/.test(value)) return;
		this.setState((prev) => ({
			draftScores: {
				...prev.draftScores,
				[playerID]: {
					...prev.draftScores[playerID],
					[holeNumber]: value,
				},
			},
		}));
	}

	async saveCorrections(roundID) {
		const { draftScores, originalScores } = this.state;
		if (!draftScores || !originalScores) return;
		this.setState({ saving: true });
		try {
			for (const playerID of Object.keys(draftScores)) {
				for (let hole = 1; hole <= 18; hole++) {
					const newScore = parseDraftScore(draftScores[playerID][hole]);
					const oldScore = parseDraftScore(originalScores[playerID][hole]);
					if (newScore === oldScore) continue;
					if (newScore == null) {
						if (oldScore != null) {
							ewtStore.deleteScore({
								roundID,
								playerID,
								num: hole,
							});
						}
					} else {
						await ewtStore.setScore(roundID, playerID, hole, newScore);
					}
				}
			}
			this.cancelEdit();
		} finally {
			this.setState({ saving: false });
		}
	}

	renderScoreCell(roundID, playerID, holeNumber, cellStyle) {
		const { editMode, draftScores } = this.state;
		if (editMode) {
			return (
				<Cell key={holeNumber} style={cellStyle}>
					<Input
						value={draftScores[playerID][holeNumber]}
						onChange={(e) =>
							this.updateDraftScore(playerID, holeNumber, e.target.value)
						}
						style={{ width: 42, padding: '0 2px', textAlign: 'center' }}
					/>
				</Cell>
			);
		}
		const holeScore =
			ewtStore.roundScores[roundID] &&
			ewtStore.roundScores[roundID][playerID] &&
			ewtStore.roundScores[roundID][playerID][holeNumber];
		return (
			<Cell key={holeNumber} style={cellStyle}>
				{holeScore && holeScore.score}
			</Cell>
		);
	}

	render() {
		const tieBreakerMode = this.props.tieBreakerMode;
		const roundID = this.getRoundID();
		var round = ewtStore.rounds[roundID];
		if (!round) return <div></div>
		var course = ewtStore.courses[round.courseID];
		if (!course) return <div></div>

		const cellStyle = {border:'1px solid lightgray',textAlign:'center',width:20,padding:1}
		const isAdmin = ewtStore.player && ewtStore.player.admin;
		const { editMode, saving } = this.state;

		const inModal = ewtStore.showCourseCard || tieBreakerMode;
		const containerStyle = {
			marginTop: 5,
			height: inModal ? '100%' : ewtStore.panelHeight,
			overflow: 'auto',
			boxSizing: 'border-box',
		};

		if (tieBreakerMode) {
			const holesByHandicap = ewtStore.getHolesByHandicap(course, round);
			const tablePlayers = (ewtStore.sortedPlayers || []).filter(
				(player) => player.group
			);
			const showTopThreeTotal = holesByHandicap.length >= 3;

			const getHoleScoreDisplay = (playerID, holeNumber) => {
				const holeScore =
					ewtStore.roundScores[roundID] &&
					ewtStore.roundScores[roundID][playerID] &&
					ewtStore.roundScores[roundID][playerID][holeNumber];
				if (!holeScore || holeScore.score == null) {
					return "";
				}
				const actualNet = ewtStore.getPlayerActualNetOnHole(
					playerID,
					roundID,
					holeNumber
				);
				if (actualNet == null) {
					return "";
				}
				return `${actualNet} (${holeScore.score})`;
			};

			const netColor = (net) =>
				net < 0 ? "red" : net === 0 ? "green" : "black";

			return (
				<div style={containerStyle}>
					<StickyTable border={1} style={{fontSize:20}} stickyHeaderCount={1}>
						<Row>
							<Cell style={cellStyle}>Player</Cell>
							<Cell style={cellStyle}>Tot</Cell>
							{holesByHandicap.map((hole, holeIndex) => (
								<React.Fragment key={hole.holeNumber}>
									<Cell
										style={cellStyle}
										onClick={() => ewtStore.showHoleMap(hole.holeNumber)}
									>
										{hole.handicap} ({hole.holeNumber})
									</Cell>
									{showTopThreeTotal && holeIndex === 2 && (
										<Cell style={cellStyle}>Top 3</Cell>
									)}
								</React.Fragment>
							))}
						</Row>
						<Row>
							<Cell style={cellStyle}>Par</Cell>
							<Cell style={cellStyle}></Cell>
							{holesByHandicap.map((hole, holeIndex) => (
								<React.Fragment key={`par-${hole.holeNumber}`}>
									<Cell style={cellStyle}>
										{course.holes[hole.holeNumber].tees[round.tees].par}
									</Cell>
									{showTopThreeTotal && holeIndex === 2 && (
										<Cell style={cellStyle}></Cell>
									)}
								</React.Fragment>
							))}
						</Row>
						{ewtStore.initialized && tablePlayers.map((sortedPlayer, index) => {
							const player = ewtStore.players[sortedPlayer.id];
							if (!player) return null;
							const playerID = player.id;
							const netTotal = ewtStore.getPlayerRoundNetTotal(playerID, roundID);
							const netTotalStr = ewtStore.formatNetTotal(netTotal);
							const topThreeTotals = ewtStore.getPlayerThreeHardestHolesTotals(
								playerID,
								roundID,
								holesByHandicap
							);
							const topThreeDisplay =
								topThreeTotals == null
									? ""
									: `${topThreeTotals.net} (${topThreeTotals.gross})`;
							return (
								<Row key={index}>
									<Cell
										style={Object.assign({}, cellStyle, {cursor:'pointer', textAlign:'left'})}
										onClick={() => ewtStore.showPlayerCard = player}
									>
										{player.shortName}
									</Cell>
									<Cell style={cellStyle}>
										<nobr style={{ color: netColor(netTotal) }}>
											{netTotalStr}
										</nobr>
									</Cell>
									{holesByHandicap.map((hole, holeIndex) => {
										const holeScoreDisplay = getHoleScoreDisplay(
											playerID,
											hole.holeNumber
										);
										return (
										<React.Fragment key={`${playerID}-${hole.holeNumber}`}>
											<Cell style={cellStyle}>{holeScoreDisplay}</Cell>
											{showTopThreeTotal && holeIndex === 2 && (
												<Cell style={cellStyle}>{topThreeDisplay}</Cell>
											)}
										</React.Fragment>
										);
									})}
								</Row>
							);
						})}
					</StickyTable>
				</div>
			);
		}

		var holes = new Array(9).fill(0);
		var frontPar = 0;
		var frontYardage = 0;
		for (var i=0;i<9;i++) {
			frontPar += course.holes[i+1].tees[round.tees].par;
			frontYardage += course.holes[i+1].tees[round.tees].yardage;
		}
		var backPar = 0;
		var backYardage = 0;
		for (i=9;i<18;i++) {
			backPar += course.holes[i+1].tees[round.tees].par;
			backYardage += course.holes[i+1].tees[round.tees].yardage;
		}

		const newPlayers = this.getSortedPlayers();

		return (
		<div style={containerStyle}>
			{!ewtStore.showMap && <>
			{isAdmin && (
				<div
					className="flexRow"
					style={{
						alignItems: 'center',
						justifyContent: 'space-between',
						marginBottom: 8,
						paddingRight: 8,
					}}
				>
					{editMode ? (
						<div className="flexRow" style={{ gap: 8 }}>
							<Button
								type="primary"
								loading={saving}
								onClick={() => this.saveCorrections(roundID)}
							>
								Save Corrections
							</Button>
							<Button disabled={saving} onClick={this.cancelEdit}>
								Cancel
							</Button>
						</div>
					) : (
						<div />
					)}
					{!editMode && (
						<EditFilled
							title="Correct scores"
							onClick={() => this.enterEditMode(newPlayers, roundID)}
							style={{ fontSize: 24, cursor: 'pointer', color: ewtStore.color1 }}
						/>
					)}
				</div>
			)}
			<StickyTable border={1} style={{fontSize:20}} stickyHeaderCount={1}>
				<Row>
 					<Cell style={cellStyle}>Hole</Cell>
					<Cell style={cellStyle} onClick={(e) => !editMode && ewtStore.showHoleMap(1)}>1</Cell>
					<Cell style={cellStyle} onClick={(e) => !editMode && ewtStore.showHoleMap(2)}>2</Cell>
					<Cell style={cellStyle} onClick={(e) => !editMode && ewtStore.showHoleMap(3)}>3</Cell>
					<Cell style={cellStyle} onClick={(e) => !editMode && ewtStore.showHoleMap(4)}>4</Cell>
					<Cell style={cellStyle} onClick={(e) => !editMode && ewtStore.showHoleMap(5)}>5</Cell>
					<Cell style={cellStyle} onClick={(e) => !editMode && ewtStore.showHoleMap(6)}>6</Cell>
					<Cell style={cellStyle} onClick={(e) => !editMode && ewtStore.showHoleMap(7)}>7</Cell>
					<Cell style={cellStyle} onClick={(e) => !editMode && ewtStore.showHoleMap(8)}>8</Cell>
					<Cell style={cellStyle} onClick={(e) => !editMode && ewtStore.showHoleMap(9)}>9</Cell>
					<Cell style={cellStyle}>Out</Cell>
					<Cell style={cellStyle} onClick={(e) => !editMode && ewtStore.showHoleMap(10)}>10</Cell>
					<Cell style={cellStyle} onClick={(e) => !editMode && ewtStore.showHoleMap(11)}>11</Cell>
					<Cell style={cellStyle} onClick={(e) => !editMode && ewtStore.showHoleMap(12)}>12</Cell>
					<Cell style={cellStyle} onClick={(e) => !editMode && ewtStore.showHoleMap(13)}>13</Cell>
					<Cell style={cellStyle} onClick={(e) => !editMode && ewtStore.showHoleMap(14)}>14</Cell>
					<Cell style={cellStyle} onClick={(e) => !editMode && ewtStore.showHoleMap(15)}>15</Cell>
					<Cell style={cellStyle} onClick={(e) => !editMode && ewtStore.showHoleMap(16)}>16</Cell>
					<Cell style={cellStyle} onClick={(e) => !editMode && ewtStore.showHoleMap(17)}>17</Cell>
					<Cell style={cellStyle} onClick={(e) => !editMode && ewtStore.showHoleMap(18)}>18</Cell>
					<Cell style={cellStyle}>In</Cell>
					<Cell style={cellStyle}>Total</Cell>
            	</Row>
				<Row>
					<Cell style={cellStyle}>Par</Cell>
					{holes.map((hole, index) => {
						return (<Cell key={index} style={cellStyle}>{course.holes[index+1].tees[round.tees].par}</Cell>)
					})}
					<Cell style={cellStyle}>{frontPar}</Cell>
					{holes.map((hole, index) => {
						return (<Cell key={index+10} style={cellStyle}>{course.holes[index+10].tees[round.tees].par}</Cell>)
					})}
					<Cell style={cellStyle}>{backPar}</Cell>
					<Cell style={cellStyle}>{frontPar+backPar}</Cell>
				</Row>
				<Row>
					<Cell style={cellStyle}>Hcp</Cell>
					{holes.map((hole, index) => {
						return (<Cell key={index} style={cellStyle}>{course.holes[index+1].tees[round.tees].handicap}</Cell>)
					})}
					<Cell style={cellStyle}></Cell>
					{holes.map((hole, index) => {
						return (<Cell key={index+10} style={cellStyle}>{course.holes[index+10].tees[round.tees].handicap}</Cell>)
					})}
					<Cell style={cellStyle}></Cell>
					<Cell style={cellStyle}></Cell>
				</Row>
				<Row>
					<Cell style={cellStyle}>Yards</Cell>
					{holes.map((hole, index) => {
						return (<Cell key={index} style={cellStyle}>{course.holes[index+1].tees[round.tees].yardage}</Cell>)
					})}
					<Cell style={cellStyle}>{frontYardage}</Cell>
					{holes.map((hole, index) => {
						return (<Cell key={index+10} style={cellStyle}>{course.holes[index+10].tees[round.tees].yardage}</Cell>)
					})}
					<Cell style={cellStyle}>{backYardage}</Cell>
					<Cell style={cellStyle}>{frontYardage+backYardage}</Cell>
				</Row>
				{ewtStore.initialized && newPlayers.map((player,index) => {
					var playerID = player.id;
					const totals = editMode
						? getDraftTotals(this.state.draftScores, playerID)
						: {
							F:
								ewtStore.roundScores[roundID] &&
								ewtStore.roundScores[roundID][playerID] &&
								ewtStore.roundScores[roundID][playerID]["F"],
							B:
								ewtStore.roundScores[roundID] &&
								ewtStore.roundScores[roundID][playerID] &&
								ewtStore.roundScores[roundID][playerID]["B"],
							T:
								ewtStore.roundScores[roundID] &&
								ewtStore.roundScores[roundID][playerID] &&
								ewtStore.roundScores[roundID][playerID]["T"],
						};
				return (
					<Row key={index}>
						<Cell
							style={Object.assign(cellStyle, {
								cursor: editMode ? 'default' : 'pointer',
							})}
							onClick={() => {
								if (!editMode) ewtStore.showPlayerCard = player;
							}}
						>
							{player.shortName}
						</Cell>
						{holes.map((hole, holeIndex) =>
							this.renderScoreCell(roundID, playerID, holeIndex + 1, cellStyle)
						)}
						<Cell style={cellStyle}>{totals.F}</Cell>
						{holes.map((hole, holeIndex) =>
							this.renderScoreCell(
								roundID,
								playerID,
								holeIndex + 10,
								cellStyle
							)
						)}
						<Cell style={cellStyle}>{totals.B}</Cell>
						<Cell style={cellStyle}>{totals.T}</Cell>
					</Row>
				)
			})}
          </StickyTable></>}
		  {ewtStore.showMap && <img src={ewtStore.courseMapURL} alt={'none available'} 
		  							style={{width:ewtStore.windowWidth}}></img>}
		</div>
	  );
	}
});


export default CourseCard;
